import { useTenant } from '@lib/useTenant'
import { Alert, Stack, Text } from '@mantine/core'
import {
  TenantIntegrationsSettings,
  canViewIntegrationKeys
} from '@packages/integrations'
import { useSupabase } from '@packages/supabase'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

/**
 * API keys for the tenant being viewed in admin.
 * RPCs use JWT tenant context — operator must assume the tenant first.
 */
export function TenantIntegrationsPanel() {
  const { t } = useTranslation()
  const params = useParams()
  const tenantId = params.id ?? ''
  const tenant = useTenant(tenantId)
  const { tenantId: sessionTenantId, p } = useSupabase()
  const canView = canViewIntegrationKeys(p)
  const sessionMatches =
    Boolean(sessionTenantId) && sessionTenantId === tenant.data?.id

  if (!tenantId) {
    return null
  }

  if (!canView) {
    return (
      <Alert color="gray">
        {t('admin.tenant.integrations.no_permission', {
          defaultValue:
            'Your admin session does not have permission to view integration API keys.'
        })}
      </Alert>
    )
  }

  if (!sessionMatches) {
    return (
      <Stack gap="md">
        <Alert color="blue" title={t('admin.tenant.integrations.assume_title', {
          defaultValue: 'Assume tenant to manage API keys'
        })}>
          <Text size="sm">
            {t('admin.tenant.integrations.assume_body', {
              defaultValue:
                'Integration keys are created in the target tenant context. Use “Assume tenant” on the Overview tab, then return to this tab.'
            })}
          </Text>
        </Alert>
        <Text size="sm" c="dimmed">
          {t('admin.tenant.integrations.tenant_label', {
            defaultValue: 'Tenant: {{name}} ({{id}})',
            name: tenant.data?.name ?? '—',
            id: tenantId
          })}
        </Text>
      </Stack>
    )
  }

  return (
    <Stack gap="md" data-testid="admin-tenant-integrations">
      <Text size="sm" c="dimmed">
        {t('admin.tenant.integrations.intro', {
          defaultValue:
            'Managing API keys for {{name}}. Keys apply to partner embeds and the integration_gateway JSON API.',
          name: tenant.data?.name ?? ''
        })}
      </Text>
      <TenantIntegrationsSettings
        showEmbedDocs={tenant.data?.type === 'forms'}
        embedBasePath="/embed/forms"
        titleOrder={5}
      />
    </Stack>
  )
}
